import api from '../../../services/api';
import {
  setOrderItems,
  setFilteredOrderItems,
  setLoading,
  setError,
  setPagination,
  setFilters,
  clearFilters,
  setKpiMetrics,
  setSyncLoading,
  setSyncError
} from '../reducers';
import dayjs from 'dayjs';
import { message } from 'antd';

// Hàm lấy danh sách các Shopify order items với phân trang và filters
export const fetchOrderItems = (page = 1, pageSize, updateUrl = null) => async (dispatch, getState) => {
  dispatch(setLoading(true));
  dispatch(setOrderItems([]));
  
  try {
    const { filters, pagination } = getState().shopifyOrders;
    const params = new URLSearchParams();

    const currentPageSize = pageSize || pagination.pageSize;
    params.append('page', page);
    params.append('pageSize', currentPageSize);

    // Always sort by newest first
    params.append('sortBy', 'purchasedAt');
    params.append('sortOrder', 'DESC');

    // Add filters
    const appendFilter = (key, value) => {
      if (Array.isArray(value)) {
        if (value.length > 0) value.forEach(v => params.append(key, v));
      } else if (value && value !== 'all') {
        params.append(key, value);
      }
    };

    if (filters.searchText) {
      params.append('search', filters.searchText);
    }
    if (filters.selectedDateRange && filters.selectedDateRange.length === 2) {
      const startDate = dayjs(filters.selectedDateRange[0]).format('YYYY-MM-DD');
      const endDate = dayjs(filters.selectedDateRange[1]).format('YYYY-MM-DD');
      params.append('startDate', startDate);
      params.append('endDate', endDate);
    }
    appendFilter('shopifyStoreId', filters.selectedStore);
    appendFilter('financialStatus', filters.selectedFinancialStatus);
    appendFilter('fulfillmentStatus', filters.selectedFulfillmentStatus);
    appendFilter('shipmentStatus', filters.selectedShipmentStatus);
    appendFilter('shipmentDetail', filters.selectedShipmentDetail);
    appendFilter('confirmShipment', filters.selectedConfirmShipment);
    appendFilter('categoryId', filters.selectedCategory);
    appendFilter('collectionId', filters.selectedCollection);
    appendFilter('printerId', filters.selectedPrinter);
    appendFilter('supporterId', filters.selectedSupporter);
    if (filters.selectedTitle && filters.selectedTitle.trim()) {
      params.append('title', filters.selectedTitle.trim());
    }
    if (filters.selectedSku && filters.selectedSku.trim()) {
      params.append('sku', filters.selectedSku.trim());
    }
    if (filters.selectedEmployeeId && filters.selectedEmployeeId.trim()) {
      params.append('employeeId', filters.selectedEmployeeId.trim());
    }
    if (filters.selectedExcludeTitle && filters.selectedExcludeTitle.trim()) {
      params.append('excludeTitle', filters.selectedExcludeTitle.trim());
    }
    if (filters.selectedExcludeSku && filters.selectedExcludeSku.trim()) {
      params.append('excludeSku', filters.selectedExcludeSku.trim());
    }
    if (filters.selectedBasecost && filters.selectedBasecost !== 'all') {
      params.append('basecost', filters.selectedBasecost);
    }

    const response = await api.get(`/shopify/orders/items?${params.toString()}`);
    
    dispatch(setOrderItems(response.data.items));
    dispatch(setPagination({
      current: response.data.pagination.page,
      pageSize: response.data.pagination.pageSize,
      total: response.data.pagination.total
    }));

    // Update URL if provided
    if (updateUrl) {
      updateUrl(filters, {
        current: response.data.pagination.page,
        pageSize: response.data.pagination.pageSize,
        total: response.data.pagination.total
      });
    }

    dispatch(setLoading(false));
  } catch (error) {
    console.error('Error fetching Shopify order items:', error);
    dispatch(setError(error.message));
    dispatch(setLoading(false));
  }
};

// Fetch KPI metrics
export const fetchKpiMetrics = () => async (dispatch, getState) => {
  try {
    const { filters } = getState().shopifyOrders;
    const params = new URLSearchParams();

    // Keep KPI filters consistent with the list endpoint
    const appendFilter = (key, value) => {
      if (Array.isArray(value)) {
        if (value.length > 0) value.forEach(v => params.append(key, v));
      } else if (value && value !== 'all') {
        params.append(key, value);
      }
    };

    if (filters.searchText) {
      params.append('search', filters.searchText);
    }

    if (filters.selectedDateRange && filters.selectedDateRange.length === 2) {
      const startDate = dayjs(filters.selectedDateRange[0]).format('YYYY-MM-DD');
      const endDate = dayjs(filters.selectedDateRange[1]).format('YYYY-MM-DD');
      params.append('startDate', startDate);
      params.append('endDate', endDate);
    }
    appendFilter('shopifyStoreId', filters.selectedStore);
    appendFilter('financialStatus', filters.selectedFinancialStatus);
    appendFilter('fulfillmentStatus', filters.selectedFulfillmentStatus);
    appendFilter('shipmentStatus', filters.selectedShipmentStatus);
    appendFilter('shipmentDetail', filters.selectedShipmentDetail);
    appendFilter('confirmShipment', filters.selectedConfirmShipment);
    appendFilter('categoryId', filters.selectedCategory);
    appendFilter('collectionId', filters.selectedCollection);
    appendFilter('printerId', filters.selectedPrinter);
    appendFilter('supporterId', filters.selectedSupporter);
    if (filters.selectedTitle && filters.selectedTitle.trim()) {
      params.append('title', filters.selectedTitle.trim());
    }
    if (filters.selectedSku && filters.selectedSku.trim()) {
      params.append('sku', filters.selectedSku.trim());
    }
    if (filters.selectedEmployeeId && filters.selectedEmployeeId.trim()) {
      params.append('employeeId', filters.selectedEmployeeId.trim());
    }
    if (filters.selectedExcludeTitle && filters.selectedExcludeTitle.trim()) {
      params.append('excludeTitle', filters.selectedExcludeTitle.trim());
    }
    if (filters.selectedExcludeSku && filters.selectedExcludeSku.trim()) {
      params.append('excludeSku', filters.selectedExcludeSku.trim());
    }
    if (filters.selectedBasecost && filters.selectedBasecost !== 'all') {
      params.append('basecost', filters.selectedBasecost);
    }

    const response = await api.get(`/shopify/orders/kpi-metrics?${params.toString()}`);
    dispatch(setKpiMetrics(response.data));
  } catch (error) {
    console.error('Error fetching KPI metrics:', error);
  }
};

// Filter handlers
export const handleSearchChange = (searchText) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, searchText }));
};

export const handleDateRangeChange = (selectedDateRange) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, selectedDateRange }));
};

export const handleStoreChange = (selectedStore) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, selectedStore }));
};

export const handleFinancialStatusChange = (selectedFinancialStatus) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, selectedFinancialStatus }));
};

export const handleFulfillmentStatusChange = (selectedFulfillmentStatus) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, selectedFulfillmentStatus }));
};

export const handleShipmentStatusChange = (selectedShipmentStatus) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, selectedShipmentStatus }));
};

export const handleShipmentDetailChange = (selectedShipmentDetail) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  const nextFilters = { ...filters, selectedShipmentDetail };
  if (selectedShipmentDetail === 'stale_over_3_days') {
    nextFilters.selectedShipmentStatus = ['in_transit'];
  }
  dispatch(setFilters(nextFilters));
};

export const handleConfirmShipmentChange = (selectedConfirmShipment) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, selectedConfirmShipment }));
};

export const handleCategoryChange = (selectedCategory) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, selectedCategory }));
};

export const handleCollectionChange = (selectedCollection) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, selectedCollection }));
};

export const handlePrinterChange = (selectedPrinter) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, selectedPrinter }));
};

export const handleSupporterChange = (selectedSupporter) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, selectedSupporter }));
};

export const handleTitleChange = (selectedTitle) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, selectedTitle }));
};

export const handleSkuChange = (selectedSku) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, selectedSku }));
};

export const handleEmployeeIdChange = (selectedEmployeeId) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, selectedEmployeeId }));
};

export const handleExcludeTitleChange = (selectedExcludeTitle) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, selectedExcludeTitle }));
};

export const handleExcludeSkuChange = (selectedExcludeSku) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, selectedExcludeSku }));
};

export const handleBasecostChange = (selectedBasecost) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  dispatch(setFilters({ ...filters, selectedBasecost }));
};

export const handleClearFilters = (updateUrl = null) => (dispatch) => {
  dispatch(clearFilters());
  const newPagination = { current: 1, total: 0 };
  dispatch(setPagination(newPagination));

  if (updateUrl) {
    const clearedFilters = {
      searchText: '',
      selectedDateRange: null,
      selectedStore: [],
      selectedFinancialStatus: [],
      selectedFulfillmentStatus: [],
      selectedShipmentStatus: [],
      selectedShipmentDetail: 'all',
      selectedConfirmShipment: 'all',
      selectedCategory: [],
      selectedCollection: [],
      selectedPrinter: [],
      selectedSupporter: [],
      selectedBasecost: 'all'
    };
    updateUrl(clearedFilters, newPagination);
  }

  dispatch(fetchOrderItems(1));
  dispatch(fetchKpiMetrics());
};

export const applyFilters = (updateUrl = null) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  const newPagination = { current: 1, total: 0 };
  dispatch(setPagination(newPagination));

  if (updateUrl) {
    updateUrl(filters, newPagination);
  }

  dispatch(fetchOrderItems(1));
  dispatch(fetchKpiMetrics());
};

export const handlePaginationChange = (page, pageSize, updateUrl = null) => (dispatch, getState) => {
  const { filters } = getState().shopifyOrders;
  
  const newPagination = { current: page, pageSize };
  dispatch(setPagination(newPagination));

  if (updateUrl) {
    updateUrl(filters, newPagination);
  }

  dispatch(fetchOrderItems(page, pageSize));
};

/**
 * Sync Shopify orders from stores
 * @param {Object} params - Sync parameters
 * @param {Array<number>} params.storeIds - Array of Shopify store IDs
 * @param {string} params.syncPeriod - Sync period (last7Days, last30Days, lastMonth, custom)
 * @param {string} params.startDate - Start date for custom period (ISO format)
 * @param {string} params.endDate - End date for custom period (ISO format)
 * @param {Array<string>} params.shopifyOrderNames - Optional array of specific order names (max 50)
 */
export const syncShopifyOrders = (params) => async (dispatch) => {
  dispatch(setSyncLoading(true));
  dispatch(setSyncError(null));

  try {
    const response = await api.post('/shopify/orders/sync-orders', params);
    
    dispatch(setSyncLoading(false));
    
    if (response.data.success) {
      message.success(response.data.message || 'Đã khởi tạo sync job thành công');
      return response.data;
    } else {
      message.error(response.data.message || 'Sync job failed');
      throw new Error(response.data.message || 'Sync failed');
    }
  } catch (error) {
    dispatch(setSyncLoading(false));
    
    const errorMessage = error.response?.data?.message || error.message || 'Lỗi khi sync Shopify orders';
    dispatch(setSyncError(errorMessage));
    
    message.error(errorMessage);
    throw error;
  }
};

/**
 * Confirm shipment for Shopify line items
 * @param {Array<number>} lineItemIds - Array of Shopify line item IDs
 * @param {Object} pagination - Current pagination state
 */
export const confirmShopifyShipment = (lineItemIds, pagination) => async (dispatch) => {
  try {
    const response = await api.post('/shopify/orders/confirm-shipment', { lineItemIds });
    
    if (response.data.success) {
      message.success(`Đã khởi tạo confirm shipment job thành công. Request ID: ${response.data.requestId}`);
      
      // Refresh list
      const currentPage = pagination?.current || 1;
      const pageSize = pagination?.pageSize || 50;
      dispatch(fetchOrderItems(currentPage, pageSize));
      dispatch(fetchKpiMetrics());
      
      return response.data;
    } else {
      message.error(response.data.message || 'Confirm shipment failed');
      throw new Error(response.data.message || 'Confirm shipment failed');
    }
  } catch (error) {
    const errorMessage = error.response?.data?.message || error.message || 'Lỗi khi confirm shipment';
    message.error(errorMessage);
    throw error;
  }
};
